import React, { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Download, Search, Eye, Upload, ChevronLeft, ChevronRight } from "lucide-react";
import TransactionDetailView from "../components/transaction-detail-view";
import EmailReportDialog from "../components/email-report-dialog";
// import { ObjectUploader } from "@/components/ObjectUploader"; // Temporarily disabled
import { api } from "../lib/api";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { set } from "date-fns";

export default function Tickets() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isImporting, setIsImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showOrderSelectDialog, setShowOrderSelectDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string>("");
  const { toast } = useToast();

  const { data: orders } = useQuery<any[]>({
    queryKey: ["/api/orders"],
  });

  // Import sessions mutation using file upload
  const importSessionsMutation = useMutation({
    mutationFn: async ({ file, orderId, orderNumber }: { file: File, orderId?: string, orderNumber?: string }) => {
      const formData = new FormData();
      formData.append('dataFile', file);
      if (orderId) {
        formData.append('orderId', orderId);
      }
      if (orderNumber) {
        formData.append('orderNumber', orderNumber);
      }
      
      const response = await fetch('/api/weighbridges/sessions/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'X-Tenant-Id': localStorage.getItem('tenant_id') || '',
        },
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || 'Failed to import sessions');
      }
      
      return response.json();
    },
    onSuccess: (results) => {
      setIsImporting(false);
      const count = results.data?.length || 0;
      const mineVisits = results.mineVisits || 0;
      const stagingVisits = results.stagingVisits || 0;
      
      toast({
        title: "Import Complete",
        description: `Successfully imported ${count} weighbridge sessions from ${results.parsing?.sheetUsed || 'file'}. ${mineVisits} vehicles added to your board${stagingVisits > 0 ? ` + ${stagingVisits} sent to staging boards` : ''}.`,
      });
      
      queryClient.invalidateQueries({ queryKey: ['/api/sessions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/kanban/trucks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/kanban/statistics'] });
      
      console.log('Import successful:', results);
      console.log('Parsing info:', results.parsing);
      console.log(`Mine visits: ${mineVisits}, Staging visits: ${stagingVisits}`);
      if (stagingVisits > 0) {
        console.log('Staging users will see these trucks with mine reference badge');
      }
    },
    onError: (error) => {
      setIsImporting(false);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import sessions",
        variant: "destructive",
      });
    },
  });

  const triggerFileUpload = () => {
    setShowOrderSelectDialog(true);
  };
  const handleOrderSelectedAndPickFile = () => {
    
    let zomgNumber = orders?.find((o: any) => o.id.toString() === selectedOrderId)?.orderNumber || ""
    
    setSelectedOrderNumber(zomgNumber);
    setShowOrderSelectDialog(false);
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      toast({
        title: "Invalid File Type",
        description: "Please select an Excel (.xlsx, .xls) or CSV file",
        variant: "destructive"
      });
      return;
    }

    console.log('📁 Uploading file:', file.name, 'Size:', file.size, 'bytes');
    setIsImporting(true);
    
    importSessionsMutation.mutate({ 
      file: file, 
      orderId: selectedOrderId || undefined,
      orderNumber: selectedOrderNumber || undefined
    });
    
    setSelectedOrderId("");
    setSelectedOrderNumber("");
    event.target.value = '';
  };

  // Fetch real transaction data from API
  const { data: sessions, isLoading } = useQuery({
    queryKey: ["/api/sessions"],
    queryFn: api.getSessions,
  });

  // Transform session data for display with comprehensive weighbridge data
  const allTransactions = (sessions || []).map((session: any) => ({
    id: session.id,
    transactionNo: session.id?.toString() || "N/A",
    date: session.createdAt ? new Date(session.createdAt).toLocaleDateString() : "N/A",
    time: session.createdAt ? new Date(session.createdAt).toLocaleTimeString() : "N/A",
    orderNo: session.orderExternalId || session.clientOrderNumber || "N/A",
    sessionClientOrderNumber: session.sessionClientOrderNumber || "-",
    ticketNo: session.ticketNo || "N/A",
    transactionType: session.entryMethod === "manual" ? "MANUAL" : "AUTO",
    
    // Vehicle information
    vehicleReg: session.vehiclePlateNumber || session.plateNumber || "N/A",
    trailer1PlateNumber: session.trailer1PlateNumber,
    trailer2PlateNumber: session.trailer2PlateNumber,
    
    // Driver information 
    driverName: session.driverName || "Unknown",
    driverId: session.driverId,
    
    // Weight measurements
    tareWeight: parseFloat(session.tareWeight || "0"),
    grossWeight: parseFloat(session.grossWeight || "0"),
    netWeight: parseFloat(session.netWeight || "0"),
    unit: session.unit || "kg",
    
    // Comprehensive operational data
    weighbridgeName: session.weighbridgeName || "Main Scale",
    customerName: session.customerName,
    freightCompany: session.customerName, // Using customerName as freight company for now
    transporterName: session.supplier || session.transporterName,
    
    // Timing information
    entryTime: session.entryTime,
    exitTime: session.exitTime,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    shift: session.shift,
    operator: session.operator,
    
    // Calculate duration and hours in lot
    duration: session.endedAt && session.startedAt ? 
      Math.round((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60000) + " min" : "N/A",
    
    // Status information
    status: session.status || "active",
    visitStatus: session.visitStatus,
    kanbanStatus: session.kanbanStatus,
    accessDecision: session.accessDecision,
    
    // Additional operational data
    supplierExternalId: session.supplierExternalId,
    sealNumber: session.sealNumber,
    weighbridgeEntryWeight: session.weighbridgeEntryWeight,
    weighbridgeExitWeight: session.weighbridgeExitWeight,
    deliveryPointEntryWeight: session.deliveryPointEntryWeight,
    deliveryPointExitWeight: session.deliveryPointExitWeight
  }));

  const generateSummaryReport = () => {
    const totalTransactions = allFilteredTransactions.length;
    const totalNet = allFilteredTransactions.reduce((sum, t) => sum + t.netWeight, 0);
    const totalGross = allFilteredTransactions.reduce((sum, t) => sum + t.grossWeight, 0);
    const totalTare = allFilteredTransactions.reduce((sum, t) => sum + t.tareWeight, 0);

    const reportContent = `
WEIGHBRIDGE TRANSACTION REPORT                               WEIGH8 SYSTEM
Generated On: ${new Date().toLocaleString()}
Filter Applied: ${operatorFilter === "all" ? "All Operators" : `Operator: ${operatorFilter}`} | ${statusFilter === "all" ? "All Status" : `Status: ${statusFilter}`}
Search Term: ${searchTerm || "None"}

${"-".repeat(120)}
   DATE          TIME     TRANS ID    OPERATOR    ORDER NO       VEHICLE      DRIVER         WEIGHBRIDGE    TARE(kg)   GROSS(kg)   NET(kg)    STATUS
${"-".repeat(120)}
${allFilteredTransactions.map(t => 
  `${t.date.padEnd(12)} ${t.time.padEnd(8)} ${t.transactionNo.padEnd(10)} ${t.operator.padEnd(10)} ${t.orderNo.padEnd(13)} ${t.vehicleReg.padEnd(11)} ${t.driverName.slice(0,13).padEnd(13)} ${(t.weighbridgeName || "Main Scale").slice(0,13).padEnd(13)} ${t.tareWeight.toLocaleString().padStart(9)} ${t.grossWeight.toLocaleString().padStart(11)} ${t.netWeight.toLocaleString().padStart(10)} ${t.status.padEnd(8)}`
).join('\n')}
${"-".repeat(120)}

SUMMARY:
   Total Transactions:     ${totalTransactions.toLocaleString()}
   Total Tare Weight:      ${(totalTare/1000).toFixed(2)} tonnes
   Total Gross Weight:     ${(totalGross/1000).toFixed(2)} tonnes
   Total Net Weight:       ${(totalNet/1000).toFixed(2)} tonnes
   Average Net per Load:   ${totalTransactions > 0 ? (totalNet/totalTransactions/1000).toFixed(2) : '0'} tonnes

Generated by Weigh8 - Professional Weighbridge Management System
    `.trim();

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Transaction_Report_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const generateExcelReport = () => {
    const headers = [
      "Transaction ID", "Date", "Time", "Operator", "Order No", "Vehicle Reg", 
      "Driver Name", "Weighbridge Name", "Tare Weight (kg)", "Gross Weight (kg)", 
      "Net Weight (kg)", "Unit", "Status", "Duration", "Entry Method"
    ];
    
    const csvContent = [
      headers.join(","),
      ...allFilteredTransactions.map(t => [
        t.transactionNo,
        t.date,
        t.time,
        t.operator,
        t.orderNo,
        t.vehicleReg,
        t.driverName,
        t.weighbridgeName || "Main Scale",
        t.tareWeight,
        t.grossWeight,
        t.netWeight,
        t.unit,
        t.status,
        t.duration,
        t.transactionType
      ].map(field => `"${field}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Transactions_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Filter transactions based on all criteria
  const allFilteredTransactions = allTransactions.filter(transaction => {
    // Search filter
    if (searchTerm && 
        !transaction.transactionNo.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !transaction.vehicleReg.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.driverName.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !transaction.orderNo.toLowerCase().includes(searchTerm.toLowerCase())
    ) return false;
    
    // Operator filter
    if (operatorFilter !== "all" && transaction.operator !== operatorFilter) return false;
    
    // Status filter
    if (statusFilter !== "all" && transaction.status !== statusFilter) return false;
    
    return true;
  });

  // Pagination logic
  const totalItems = allFilteredTransactions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredTransactions = allFilteredTransactions.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, operatorFilter, statusFilter]);

  // Get unique operators from data
  const operators = Array.from(new Set(allTransactions.map(t => t.operator))).filter(op => op && op !== "Unknown");

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)] flex items-center gap-2">
            <FileText className="w-8 h-8" />
            Transaction History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} transactions {totalItems > 0 && `• Total in system: ${allTransactions.length}`}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            data-testid="input-file-upload"
            aria-label="Upload Excel file"
          />
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={triggerFileUpload}
            disabled={isImporting}
            data-testid="button-upload-transactions"
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
          >
            <Upload className="w-4 h-4 mr-2" />
            {isImporting ? "Importing..." : "Upload"}
          </Button>

          <Button onClick={generateSummaryReport} variant="outline" size="sm" data-testid="button-export-report" className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>

          <Button onClick={generateExcelReport} variant="outline" size="sm" data-testid="button-export-excel" className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]">
            <FileText className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          
          <EmailReportDialog transactions={filteredTransactions} />
        </div>
      </div>
      
      {/* Controls Section */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search and Filters */}
        <div className="flex flex-1 items-center gap-3">
          <div className="relative flex-1 min-w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(215,20%,45%)]" />
            <Input
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
              data-testid="input-search-transaction"
            />
          </div>
          
          <Select value={operatorFilter} onValueChange={setOperatorFilter}>
            <SelectTrigger className="w-40 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
              <SelectValue placeholder="All Operators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Operators</SelectItem>
              {operators.map(operator => (
                <SelectItem key={operator} value={operator}>{operator}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

      </div>

      {/* Order Selection Dialog */}
      <Dialog open={showOrderSelectDialog} onOpenChange={setShowOrderSelectDialog}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Select Order</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Select an order to attach to your upload. This order number will be assigned to all vehicles in the Excel file.
                </p>
                <div className="space-y-2">
                  <label htmlFor="order-select" className="text-sm font-medium">
                    Order
                  </label>
                  <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                    <SelectTrigger id="order-select">
                      <SelectValue placeholder="Select an order *" />
                    </SelectTrigger>
                    <SelectContent>
                      {orders?.map((order: any) => (
                        <SelectItem key={order.id} value={order.id.toString()}>
                          {order.orderNumber} - {order.product} ({order.clientName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedOrderId ? (
                    <p className="text-xs text-green-600">
                      ✓ Order selected
                    </p>
                  ) : (
                    <p className="text-xs text-red-600">
                      Please select an order to continue
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Next: You'll be asked to select an Excel file to upload
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowOrderSelectDialog(false);
                    setSelectedOrderId("");
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleOrderSelectedAndPickFile} disabled={!selectedOrderId}>
                  Continue to File Selection
                </Button>
              </div>
            </DialogContent>
      </Dialog>

      {/* Transactions Table */}
      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardHeader>
          <CardTitle className="text-[hsl(187,100%,42%)]">Transaction Records ({filteredTransactions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead className="bg-[hsl(215,28%,14%)] border-b border-[hsl(215,28%,18%)]">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order Number</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Client Order Number</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Transporter</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Number Plate</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Customer</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">ID</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tare</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Gross</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Net</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredTransactions.map((transaction) => {
                        // Calculate hours in lot
                        const hoursInLot = transaction.exitTime && transaction.entryTime 
                          ? Math.round((new Date(transaction.exitTime).getTime() - new Date(transaction.entryTime).getTime()) / (1000 * 60 * 60) * 10) / 10
                          : transaction.entryTime 
                            ? Math.round((new Date().getTime() - new Date(transaction.entryTime).getTime()) / (1000 * 60 * 60) * 10) / 10
                            : 0;
                        
                        return (
                        <tr key={transaction.id} className="hover:bg-muted/30">
                          {/* Date */}
                          <td className="px-3 py-3">
                            <div className="text-sm">
                              {transaction.startedAt ? new Date(transaction.startedAt).toLocaleDateString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleDateString() : transaction.date)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {transaction.endedAt ? new Date(transaction.endedAt).toLocaleTimeString() : (transaction.entryTime ? new Date(transaction.entryTime).toLocaleTimeString() : transaction.time)}
                            </div>
                          </td>
                          
                          {/* Order Number */}
                          <td className="px-3 py-3">
                            <div className="text-sm font-mono">{transaction.orderNo || '-'}</div>
                          </td>

                          {/* Order Number */}
                          <td className="px-3 py-3">
                            <div className="text-sm font-mono">{transaction.sessionClientOrderNumber || '-'}</div>
                          </td>
                          
                          {/* Transporter */}
                          <td className="px-3 py-3">
                            <div className="text-sm">{transaction.transporterName || '-'}</div>
                          </td>
                          
                          {/* Number Plate */}
                          <td className="px-3 py-3">
                            <div className="font-medium text-sm" data-testid={`vehicle-${transaction.id}`}>
                              {transaction.vehicleReg}
                            </div>
                            {transaction.trailer1PlateNumber && (
                              <div className="text-xs text-muted-foreground">T1: {transaction.trailer1PlateNumber}</div>
                            )}
                            {transaction.trailer2PlateNumber && (
                              <div className="text-xs text-muted-foreground">T2: {transaction.trailer2PlateNumber}</div>
                            )}
                          </td>
                          
                          {/* customer */}
                          <td className="px-3 py-3">
                            <div className="text-sm font-medium">{transaction.customerName}</div>
                          </td>
                          
                          {/* ID */}
                          <td className="px-3 py-3">
                            <div className="text-sm font-mono">{transaction.driverId || '-'}</div>
                          </td>
                          
                          {/* Tare */}
                          <td className="px-3 py-3">
                            <span className="text-sm font-medium">
                              {transaction.tareWeight ? `${transaction.tareWeight.toLocaleString()} ${transaction.unit}` : '-'}
                            </span>
                          </td>
                          
                          {/* Gross */}
                          <td className="px-3 py-3">
                            <span className="text-sm font-medium">
                              {transaction.grossWeight ? `${transaction.grossWeight.toLocaleString()} ${transaction.unit}` : '-'}
                            </span>
                          </td>
                          
                          {/* Net */}
                          <td className="px-3 py-3">
                            <span className="font-bold text-green-600">
                              {transaction.netWeight ? `${transaction.netWeight.toLocaleString()} ${transaction.unit}` : '-'}
                            </span>
                          </td>
                          
                          {/* Actions */}
                          <td className="px-3 py-3">
                            <Dialog open={isDetailViewOpen && selectedTransaction?.id === transaction.id} onOpenChange={(open) => {
                              if (!open) {
                                setIsDetailViewOpen(false);
                                setSelectedTransaction(null);
                              }
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTransaction(transaction);
                                    setIsDetailViewOpen(true);
                                  }}
                                  data-testid={`button-view-details-${transaction.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Details
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Transaction Details & Images</DialogTitle>
                                </DialogHeader>
                                {selectedTransaction && (
                                  <TransactionDetailView transaction={selectedTransaction} />
                                )}
                              </DialogContent>
                            </Dialog>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>No transactions found matching your search criteria.</p>
                  <p className="text-sm mt-2">Try adjusting your filters or search terms.</p>
                </div>
              )}

            {/* Pagination Controls */}
            {totalItems > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Show</span>
                  <Select 
                    value={itemsPerPage.toString()} 
                    onValueChange={(value) => {
                      setItemsPerPage(parseInt(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span>per page</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    data-testid="button-previous-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const showPages = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
                      let endPage = Math.min(totalPages, startPage + showPages - 1);
                      
                      if (endPage - startPage < showPages - 1) {
                        startPage = Math.max(1, endPage - showPages + 1);
                      }

                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            variant={1 === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(1)}
                            className="w-8 h-8 p-0"
                          >
                            1
                          </Button>
                        );
                        if (startPage > 2) {
                          pages.push(<span key="ellipsis1" className="px-2">...</span>);
                        }
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={i === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(i)}
                            className="w-8 h-8 p-0"
                            data-testid={`button-page-${i}`}
                          >
                            {i}
                          </Button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="ellipsis2" className="px-2">...</span>);
                        }
                        pages.push(
                          <Button
                            key={totalPages}
                            variant={totalPages === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="button-next-page"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
            </CardContent>
          </Card>
    </div>
  );
}